import { useCallback, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { useMediaQuery } from 'common/utils';
import { Button } from 'components/v2/atoms/button';
import ReactCrop, { centerCrop, Crop, makeAspectCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import CloseButton from 'components/v2/atoms/close-button';

export interface ImageEditorType {
  open: boolean;
  setOpen: React.Dispatch<boolean>;
  mediaUrl: string;
  onEditComplete: (file: File) => void;
  fixedRatio?: number;
  freeCrop?: boolean;
}

const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 100,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
};

const verticalAspect = 9 / 16;
const horizontalAspect = 16 / 9;
const squareAspect = 1 / 1;

export default function ImageEditor({
  open = false,
  setOpen,
  mediaUrl,
  onEditComplete,
  fixedRatio,
  freeCrop = false,
}: ImageEditorType): JSX.Element {
  const [crop, setCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const croppedImage = useRef(null);
  const isMobileScreen = useMediaQuery('(max-width: 767px)');
  const [aspect, setAspect] = useState<number | undefined>(
    freeCrop ? null : fixedRatio || horizontalAspect,
  );
  const [processing, setProcessing] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const onLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      imgRef.current = e.currentTarget;
      const aspect = freeCrop
        ? horizontalAspect
        : fixedRatio || horizontalAspect;
      const { width, height } = imgRef.current;
      setCrop(centerAspectCrop(width, height, aspect));
      setAspect(freeCrop ? null : fixedRatio || horizontalAspect);
    },
    [fixedRatio, freeCrop],
  );

  const onCropCompleted = async (crop: Crop) => {
    setProcessing(true);
    croppedImage.current = await makeClientCrop(crop);
    setProcessing(false);
  };

  const makeClientCrop = (crop: Crop): Promise<Blob> => {
    const canvas = document.createElement('canvas');
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = Math.ceil(crop.width * scaleX) - 7;
    canvas.height = Math.ceil(crop.height * scaleY) - 7;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY,
    );
    return new Promise((resolve) => {
      setTimeout(() => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          1,
        );
      }, 500);
    });
  };

  const handleAspectChange = async (newAspect: number) => {
    if (newAspect === aspect) return;
    try {
      croppedImage.current = null;
      const { width, height } = imgRef.current;
      let newCrop: Crop;
      switch (newAspect) {
        case squareAspect:
          newCrop = {
            x: 0,
            y: 0,
            width: Math.min(width, height),
            height: Math.min(width, height),
            unit: 'px',
          };
          break;
        case verticalAspect:
          newCrop = {
            x: 0,
            y: 0,
            width: Math.min(width * verticalAspect, width),
            height: height,
            unit: 'px',
          };
          break;
        case horizontalAspect:
          newCrop = {
            x: 0,
            y: 0,
            width: width,
            height: Math.min(width / horizontalAspect, height),
            unit: 'px',
          };
          break;
      }
      setAspect(newAspect);
      setCrop(newCrop);
      await onCropCompleted(newCrop);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleSubmitCroppedImage = async () => {
    if (!croppedImage.current) {
      croppedImage.current = await makeClientCrop(crop);
    }
    if (croppedImage.current) {
      const imgFile = new File([croppedImage.current], 'image.jpg', {
        type: 'image/jpeg',
      });
      onEditComplete(imgFile);
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobileScreen}
      scroll="paper"
      maxWidth="lg"
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      {!isMobileScreen ? (
        // Desktop version
        <div className="relative flex flex-col items-center rounded-xl bg-white p-8 text-lg shadow-lg">
          <div className="flex flex-col items-center">
            <div className="mb-5 text-center font-semibold">画像の確認</div>
            <div className="mb-6 flex flex-col items-center justify-center">
              <div className="rounded border border-gray-300">
                <ReactCrop
                  crop={crop}
                  onComplete={onCropCompleted}
                  onChange={(crop) => setCrop(crop)}
                  aspect={aspect}
                  ruleOfThirds
                  className="[&_.ord-nw::after]:border-black [&_.ord-ne::after]:border-black [&_.ord-sw::after]:border-black [&_.ord-se::after]:border-black [&_.ReactCrop__crop-selection]:border-2 [&_.ReactCrop__crop-selection]:border-white [&_.ord-ne::after]:border [&_.ord-ne::after]:bg-white [&_.ord-nw::after]:border [&_.ord-nw::after]:bg-white [&_.ord-se::after]:border [&_.ord-se::after]:bg-white [&_.ord-sw::after]:border [&_.ord-sw::after]:bg-white"
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={mediaUrl}
                    onLoad={onLoad}
                    className="min-h-[calc(30vh-100px)] min-w-[calc(30vw-300px)] max-w-full border border-gray-300 object-contain md:!max-h-[calc(100vh-300px)]"
                  />
                </ReactCrop>
              </div>
              {!fixedRatio && !freeCrop && (
                <div className="mt-3 flex flex-row justify-center">
                  <button
                    className={`cursor-pointer rounded-full border-2 border-white bg-gray-200 px-4 py-1 text-center text-xs text-gray-600 ${
                      aspect === verticalAspect ? 'bg-gray-600 text-white' : ''
                    }`}
                    onClick={() => handleAspectChange(verticalAspect)}
                  >
                    縦長
                  </button>
                  <button
                    className={`ml-2 cursor-pointer rounded-full border-2 border-white bg-gray-200 px-4 py-1 text-center text-xs text-gray-600 ${
                      aspect === squareAspect ? 'bg-gray-600 text-white' : ''
                    }`}
                    onClick={() => handleAspectChange(squareAspect)}
                  >
                    正方形
                  </button>
                  <button
                    className={`ml-2 cursor-pointer rounded-full border-2 border-white bg-gray-200 px-4 py-1 text-center text-xs text-gray-600 ${
                      aspect === horizontalAspect
                        ? 'bg-gray-600 text-white'
                        : ''
                    }`}
                    onClick={() => handleAspectChange(horizontalAspect)}
                  >
                    横長
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full items-center justify-end">
            <div className="ml-1">
              <Button
                onClick={handleSubmitCroppedImage}
                disabled={processing}
                variant="blackDefault"
                className="w-fit flex-1 rounded-full px-8 py-2 text-sm"
              >
                適用
              </Button>
            </div>
          </div>
          <CloseButton onClick={handleClose} />
        </div>
      ) : (
        // Mobile version
        <div className="flex h-screen max-h-screen w-screen flex-col overflow-y-auto rounded-none bg-white p-0 pb-8">
          <div className="flex h-16 w-full items-center border-b border-gray-200 p-3">
            <Button
              onClick={handleClose}
              variant="outlineDefault"
              className="w-fit rounded-full px-4 py-2 text-sm"
            >
              キャンセル
            </Button>

            <span className="mx-auto text-sm font-semibold">画像の確認</span>
            <Button
              onClick={handleSubmitCroppedImage}
              disabled={processing}
              variant="blackDefault"
              className="w-fit rounded-full px-8 py-2 text-sm"
            >
              適用
            </Button>
          </div>
          <div className="mt-3 flex w-full flex-col px-5">
            {!fixedRatio && !freeCrop && (
              <div className="my-3 flex flex-row justify-center">
                <button
                  className={`cursor-pointer rounded-full border-2 border-white bg-gray-200 px-4 py-1 text-center text-xs text-gray-600 ${
                    aspect === verticalAspect ? 'bg-gray-600 text-white' : ''
                  }`}
                  onClick={() => handleAspectChange(verticalAspect)}
                >
                  縦長
                </button>
                <button
                  className={`ml-2 cursor-pointer rounded-full border-2 border-white bg-gray-200 px-4 py-1 text-center text-xs text-gray-600 ${
                    aspect === squareAspect ? 'bg-gray-600 text-white' : ''
                  }`}
                  onClick={() => handleAspectChange(squareAspect)}
                >
                  正方形
                </button>
                <button
                  className={`ml-2 cursor-pointer rounded-full border-2 border-white bg-gray-200 px-4 py-1 text-center text-xs text-gray-600 ${
                    aspect === horizontalAspect ? 'bg-gray-600 text-white' : ''
                  }`}
                  onClick={() => handleAspectChange(horizontalAspect)}
                >
                  横長
                </button>
              </div>
            )}
            <div className="border border-gray-300 [&_.ReactCrop__drag-handle]:!h-10 [&_.ReactCrop__drag-handle]:!w-10">
              <ReactCrop
                crop={crop}
                onComplete={onCropCompleted}
                onChange={(crop) => setCrop(crop)}
                ruleOfThirds
                aspect={aspect}
                className="[&_.ReactCrop__crop-selection]:border-2 [&_.ReactCrop__crop-selection]:border-white"
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={mediaUrl}
                  onLoad={onLoad}
                  className="pointer-events-none max-h-full w-full border border-gray-300"
                />
              </ReactCrop>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
